import { useEffect, useState } from "react";
import { fetchAdSlots, createMyAd, fetchMyAds, createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus, DollarSign, TrendingUp, Eye, MousePointerClick, Repeat, Zap } from "lucide-react";
export default function Advertise() {
  const [slots, setSlots] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [billing, setBilling] = useState("one_time");
  const [form, setForm] = useState({ title: "", image_url: "", click_url: "", slot: "homepage_banner", channel_id: "", editorial_text: "" });
  const { user } = useAuth() || {};
  const nav = useNavigate();

  const load = async () => {
    setSlots(await fetchAdSlots());
    if (user) setMyAds(await fetchMyAds());
  };
  useEffect(() => { load(); }, [user]);

  const currentSlot = slots.find(s => s.slot === form.slot);
  const subAvailable = !!currentSlot?.sub_lookup_key;
  const effectiveBilling = subAvailable ? billing : "one_time";

  const submitAd = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to advertise"); nav("/login"); return; }
    try {
      const ad = await createMyAd({ ...form, billing: effectiveBilling });
      toast.success("Ad created. Proceeding to payment…");
      const r = await createCheckout(ad.lookup_key, { kind: "ad", ad_id: ad.id, slot: ad.slot });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const payFor = async (ad) => {
    try { const r = await createCheckout(ad.lookup_key, { kind: "ad", ad_id: ad.id, slot: ad.slot }); window.location.href = r.checkout_url; }
    catch { toast.error("Checkout failed"); }
  };

  const statusBadge = (s) => {
    const map = { draft: ["text-white/60","bg-white/10","Draft"], pending_review: ["text-yellow-400","bg-yellow-400/10","Pending Review"], active: ["text-[#D4AF37]","bg-[#D4AF37]/15","Active"], rejected: ["text-[#E11D48]","bg-[#E11D48]/15","Rejected"] };
    const [c, bg, l] = map[s] || ["text-white/60","bg-white/10", s];
    return <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${c} ${bg}`}>{l}</span>;
  };

  const totalImpr = myAds.reduce((n, a) => n + (a.impressions || 0), 0);
  const totalClicks = myAds.reduce((n, a) => n + (a.clicks || 0), 0);
  const ctr = totalImpr ? ((totalClicks / totalImpr) * 100).toFixed(2) : "0.00";

  return (
    <div className="pt-16" data-testid="advertise-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-2"><Megaphone className="w-3 h-3"/>Ad Marketplace</p>
        <h1 className="font-heading text-4xl md:text-6xl font-bold mb-3 leading-none">Advertise on VOTLiving.tv</h1>
        <p className="text-white/60 max-w-2xl mb-10">Reach a global Christian audience across 3 channels, live TV guide, news section, and on-demand library. All ads reviewed by our editorial team before going live.</p>

        {/* Purchase-channel CTA */}
        <a href="/purchase-channel" className="block mb-10 group" data-testid="purchase-channel-cta">
          <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8 shadow-[0_10px_60px_rgba(212,175,55,0.10)] transition-all group-hover:border-[#D4AF37]/60">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#D4AF37]"/>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-1">Beyond Banners — Own a Channel</p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white leading-tight">Broadcast your own channel on VOTLiving.tv</h3>
              <p className="text-sm text-white/60 mt-2">Self-service video uploads, full schedule control, custom branding. Starting at $2,999/mo.</p>
            </div>
            <div className="btn-gold rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 flex-shrink-0">Launch a Channel →</div>
          </div>
        </a>

        {/* Billing toggle */}
        <div className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-6" data-testid="billing-toggle">
          <button onClick={()=>setBilling("one_time")} className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${billing==="one_time" ? "bg-[#D4AF37] text-[#050507]" : "text-white/60 hover:text-white"}`} data-testid="billing-onetime">7-Day Placement</button>
          <button onClick={()=>setBilling("subscription")} className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${billing==="subscription" ? "bg-[#D4AF37] text-[#050507]" : "text-white/60 hover:text-white"}`} data-testid="billing-sub"><Repeat className="w-3 h-3"/>Monthly Subscription</button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-12">
          {slots.map(s => {
            const isSub = billing === "subscription" && s.sub_lookup_key;
            const isFeatured = s.slot === "featured";
            return (
              <div key={s.slot} className={`bg-[#0A0A0E] border rounded-xl p-5 ${isFeatured ? "border-[#D4AF37]/40 shadow-[0_8px_30px_rgba(212,175,55,0.15)]" : "border-white/10"}`} data-testid={`slot-${s.slot}`}>
                {isFeatured && <div className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase text-[#D4AF37] mb-2 bg-[#D4AF37]/10 px-2 py-1 rounded"><Zap className="w-3 h-3"/>Premium</div>}
                <p className="text-[10px] text-[#D4AF37] tracking-widest uppercase mb-2">{isSub ? "monthly recurring" : `${s.duration_days}-day placement`}</p>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">{s.name}</h3>
                <p className="text-xs text-white/60 leading-relaxed mb-4 min-h-[3rem]">{s.description}</p>
                <div className="flex items-baseline gap-1">
                  <DollarSign className="w-4 h-4 text-[#D4AF37]"/>
                  <span className="font-heading text-2xl font-bold text-[#D4AF37]">{isSub ? s.sub_price_usd?.toFixed(0) : s.price_usd.toFixed(0)}</span>
                  <span className="text-xs text-white/50 ml-1">USD{isSub ? "/mo" : ""}</span>
                </div>
                {billing === "subscription" && !s.sub_lookup_key && <p className="text-[10px] text-white/40 mt-2">Not available as subscription</p>}
              </div>
            );
          })}
        </div>

        {user && myAds.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mb-12" data-testid="analytics-summary">
            <StatCard icon={<Eye className="w-5 h-5"/>} label="Total Impressions" value={totalImpr.toLocaleString()} />
            <StatCard icon={<MousePointerClick className="w-5 h-5"/>} label="Total Clicks" value={totalClicks.toLocaleString()} />
            <StatCard icon={<TrendingUp className="w-5 h-5"/>} label="Click-Through Rate" value={`${ctr}%`} />
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8">
          <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
            <h3 className="font-heading text-2xl font-bold mb-4">Book a Slot</h3>
            <form onSubmit={submitAd} className="space-y-3">
              <Field placeholder="Ad title" value={form.title} onChange={v=>setForm({...form,title:v})} testid="ad-title"/>
              <Field placeholder="Image URL (banner creative)" value={form.image_url} onChange={v=>setForm({...form,image_url:v})} testid="ad-image"/>
              <Field placeholder="Click-through URL" value={form.click_url} onChange={v=>setForm({...form,click_url:v})} testid="ad-click"/>
              <select value={form.slot} onChange={e=>setForm({...form,slot:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60" data-testid="ad-slot-select">
                {slots.map(s => {
                  const isSub = billing === "subscription" && s.sub_lookup_key;
                  const disabled = billing === "subscription" && !s.sub_lookup_key;
                  const price = isSub ? s.sub_price_usd : s.price_usd;
                  return <option key={s.slot} value={s.slot} disabled={disabled}>{s.name} — ${price?.toFixed(0)}{isSub ? "/mo" : ""}</option>;
                })}
              </select>
              {form.slot === "preroll" && (
                <select value={form.channel_id} onChange={e=>setForm({...form,channel_id:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white" data-testid="ad-channel-select">
                  <option value="">Choose channel…</option>
                  <option value="ch-family">VOT Family</option>
                  <option value="ch-worship">VOT Worship</option>
                  <option value="ch-study">VOT Study</option>
                </select>
              )}
              {form.slot === "featured" && (
                <textarea placeholder="Editorial write-up (short paragraph shown in the hero placement)" rows={4} value={form.editorial_text} onChange={e=>setForm({...form,editorial_text:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="ad-editorial"/>
              )}
              <button className="w-full btn-gold py-3 rounded-full font-semibold flex items-center justify-center gap-2" data-testid="advertise-submit"><Plus className="w-4 h-4"/>Create Ad & Pay</button>
              <p className="text-[11px] text-white/40 text-center pt-2">After payment your ad enters Pending Review. Our editorial team approves ads within 24 hours.{effectiveBilling === "subscription" && " Cancel any time from your account."}</p>
            </form>
          </div>

          <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
            <h3 className="font-heading text-2xl font-bold mb-4">Your Ads</h3>
            {!user ? <p className="text-white/60 text-sm">Sign in to see your campaigns.</p>
              : myAds.length === 0 ? <p className="text-white/60 text-sm" data-testid="no-ads">You haven't booked any ads yet.</p>
              : (
                <div className="space-y-3">
                  {myAds.map(ad => (
                    <div key={ad.id} className="flex gap-3 bg-white/[0.03] border border-white/10 rounded-xl p-3" data-testid={`myad-${ad.id}`}>
                      <img src={ad.image_url} alt="" className="w-24 h-16 object-cover rounded-lg border border-white/10 flex-shrink-0" onError={e=>e.target.style.display='none'}/>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-heading font-semibold text-white truncate">{ad.title}</p>
                          {statusBadge(ad.status)}
                          {ad.billing === "subscription" && <span className="text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] flex items-center gap-1"><Repeat className="w-2.5 h-2.5"/>MONTHLY</span>}
                        </div>
                        <p className="text-[11px] text-white/50 mt-1">{ad.slot} · ${ad.price_usd?.toFixed(0)}{ad.billing === "subscription" ? "/mo" : ""} · {ad.payment_status}</p>
                        {(ad.impressions || ad.clicks) && (
                          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/60" data-testid={`ad-analytics-${ad.id}`}>
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3"/>{ad.impressions || 0}</span>
                            <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3"/>{ad.clicks || 0}</span>
                            <span className="text-white/40">CTR {ad.impressions ? ((ad.clicks/ad.impressions)*100).toFixed(1) : "0.0"}%</span>
                          </div>
                        )}
                        {ad.payment_status !== "paid" && (
                          <button onClick={()=>payFor(ad)} className="mt-2 text-xs btn-gold px-3 py-1 rounded-full" data-testid={`pay-ad-${ad.id}`}>Pay now →</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ testid, ...props }) {
  return <input required {...props} onChange={e=>props.onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid={testid}/>;
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-2 text-[#D4AF37] mb-2">{icon}<span className="text-[10px] font-semibold tracking-widest uppercase">{label}</span></div>
      <p className="font-heading text-3xl font-bold text-white">{value}</p>
    </div>
  );
}
